import { DateTime } from 'luxon';
import React from 'react';
import styled from 'styled-components';

import FlexContainer from 'components/lib/ui/FlexContainer';
import ProgressBar from 'components/lib/ui/ProgressBar';
import Text from 'components/lib/ui/Text';
import { Box } from 'components/settings/referrals/Box';
import { Boxes } from 'components/settings/referrals/Boxes';
import { ReferralRedemption } from 'components/settings/referrals/ReferralRedemption';

import { spacing, variables } from 'common/lib/theme/dynamic';
import { formatCurrencyNoCents } from 'common/utils/Currency';
import { formatThousands } from 'common/utils/Number';

import type {
  ReferralEarningsStatisticsFieldsFragment,
  ReferralConstantsFieldsFragment,
  ReferralEarningsRedemptionsFieldsFragment,
} from 'common/generated/graphql';

const Root = styled(FlexContainer).attrs({ column: true })`
  gap: ${spacing.xlarge};
`;

const Progress = styled(FlexContainer).attrs({ column: true })`
  gap: ${spacing.xsmall};
`;

const Earnings = styled(FlexContainer).attrs({ column: true })`
  gap: ${spacing.default};
`;

const EarningsList = styled(FlexContainer).attrs({ column: true })`
  border-bottom: 1px solid ${variables.color.divider.primary};
`;

type Props = {
  className?: string;
  constants: ReferralConstantsFieldsFragment;
  statistics: ReferralEarningsStatisticsFieldsFragment;
  redemptions: Array<ReferralEarningsRedemptionsFieldsFragment>;
};

export const ReferralEarnings = ({ constants, className, redemptions, statistics }: Props) => {
  const { referralAnnualRewardLimitUsd } = constants;
  const currentYear = DateTime.local().startOf('year').year;

  return (
    <Root className={className}>
      <Progress>
        <FlexContainer justifyBetween alignCenter>
          <Text color="content.primary" weight="medium">
            {currentYear}
          </Text>

          <Text color="content.secondary" size="small">
            {formatCurrencyNoCents(statistics.creditsEarned)} /{' '}
            {formatCurrencyNoCents(referralAnnualRewardLimitUsd)}
          </Text>
        </FlexContainer>

        <ProgressBar
          value={statistics.creditsEarned / referralAnnualRewardLimitUsd}
          color="progress.success"
          roundAppearance
          roundInnerBar={false}
        />
      </Progress>

      <Boxes>
        <Box
          value={formatCurrencyNoCents(statistics.creditsEarned)}
          label={`Earned in ${currentYear}`}
          color="content.success"
        />
        <Box value={formatThousands(statistics.pendingCount)} label="Pending" />
        <Box value={formatThousands(statistics.earnedCreditsCount)} label="Completed" />
      </Boxes>

      {redemptions.length > 0 && (
        <Earnings>
          <Text color="content.primary" weight="medium">
            Gift cards earned
          </Text>

          <EarningsList>
            {redemptions.map((redemption) => (
              <ReferralRedemption key={redemption.id} redemption={redemption} />
            ))}
          </EarningsList>
        </Earnings>
      )}
    </Root>
  );
};
